import React, { useState, useMemo, Fragment } from "react";
import classNames from "classnames";
import { Listbox } from "@headlessui/react";
import { FiChevronDown, FiX } from "react-icons/fi";
import { usePopper } from "react-popper";
import { SelectProps } from ".";
import styles from "./index.module.css";

export const LineSelect = ({
  value: selected,
  onSelect,
  options,
  size,
  containerClassName,
  buttonClassName,
  buttonStyle,
  optionsStyle,
  placeholder,
  disabled,
  hint,
  error,
  label,
  prependOption,
  appendOption,
  onRemove,
  id,
  onResetSelect,
}: SelectProps) => {
  const [buttonElement, setButtonElement] = useState(null);
  const [optionsElement, setOptionsElement] = useState(null);
  const { styles: popperStyles, attributes } = usePopper(
    buttonElement,
    optionsElement
  );
  const shouldShowErrorMessage = useMemo(
    () => !!(typeof error === "string" && error.length),
    [error]
  );
  const shouldShowHint = useMemo(() => hint?.length, [hint]);

  const selectPropsClass = {
    [styles.selectSmall]: size === "sm",
    [styles.selectMedium]: size === "md",
    [styles.selectError]: error,
  };

  const renderHintOrError = () => {
    if (shouldShowErrorMessage) {
      return (
        <span className="float-left text-caption text-error">{error}</span>
      );
    }

    if (shouldShowHint) {
      return (
        <span className="float-left text-caption text-secondary-gray">
          {hint}
        </span>
      );
    }

    return null;
  };

  const renderSelectedValue = () => {
    if ((placeholder && !selected) || !selected?.value) {
      return placeholder;
    }

    return selected.value;
  };

  return (
    <>
      <Listbox
        value={selected}
        onChange={onSelect}
        as="div"
        className={classNames(
          styles.selectContainer,
          selectPropsClass,
          containerClassName
        )}
        disabled={disabled}
        autoFocus
      >
        {({ open }) => (
          <>
            {label && (
              <Listbox.Label
                className={classNames(styles.selectLabel, {
                  "text-error": error,
                  // 'text-orange': open,
                })}
              >
                {label}
              </Listbox.Label>
            )}
            <Listbox.Button
              className={classNames(
                styles.select,
                styles.lineSelect,
                buttonClassName,
                {
                  "border-primary": open,
                  // 'border-line-gray text-line-gray': disabled
                }
              )}
              style={buttonStyle}
              ref={setButtonElement}
              id={id}
              data-testid={id}
            >
              <span
                className={classNames(
                  "block truncate text-label-md font-medium",
                  { "text-line-gray": disabled }
                )}
              >
                {renderSelectedValue()}
              </span>
              {onResetSelect && selected.value && selected.key !== null && (
                <FiX
                  size={16}
                  className="text-orange absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer"
                  onClick={(event) => {
                    // propagation here to prevent triggering onClick on parent listbox.option
                    event.stopPropagation();
                    //onResetSelect()
                    onResetSelect(selected);
                  }}
                />
              )}
              <FiChevronDown
                size={18}
                className={classNames(
                  "flex items-center absolute transform right-0 top-1/2 -translate-y-1/2 pointer-events-none",
                  {
                    "text-secondary-gray": !disabled && !open,
                    "text-line-gray": disabled,
                    "text-primary": open,
                  }
                )}
              />
            </Listbox.Button>
            <Listbox.Options
              className={styles.options}
              style={{ ...optionsStyle, ...popperStyles.popper }}
              ref={setOptionsElement}
              {...attributes.popper}
            >
              {prependOption && (
                <Listbox.Option
                  value={selected}
                  className={classNames(styles.option, "hover:bg-fill-gray")}
                >
                  {prependOption}
                </Listbox.Option>
              )}
              {options?.map((option) => {
                const isSelected = option.key === selected?.key;
                return (
                  <Listbox.Option
                    key={option.key}
                    className={({ active }) =>
                      classNames(styles.option, {
                        "text-gray": !isSelected,
                        "text-primary bg-primary-50": isSelected,
                        "bg-fill-gray": active && !isSelected,
                      })
                    }
                    value={option}
                  >
                    <span className="block truncate">{option.value}</span>
                    {option.removable && (
                      // we use listbox.option here to close the listbox without selecting the parent listbox.option value
                      <Listbox.Option value={selected} as={Fragment}>
                        <FiX
                          size={16}
                          className="text-orange absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
                          onClick={(event) => {
                            // propagation here to prevent triggering onClick on parent listbox.option
                            event.stopPropagation();
                            onRemove(option);
                          }}
                        />
                      </Listbox.Option>
                    )}
                  </Listbox.Option>
                );
              })}
              {appendOption && (
                <Listbox.Option
                  value={selected}
                  className={classNames(styles.option, "hover:bg-fill-gray")}
                >
                  {appendOption}
                </Listbox.Option>
              )}
            </Listbox.Options>
          </>
        )}
      </Listbox>
      {(shouldShowHint || shouldShowErrorMessage) && (
        <div className="w-full h-3.5 mt-1.5">{renderHintOrError()}</div>
      )}
    </>
  );
};

LineSelect.defaultProps = {
  placeholder: "Pilih",
  size: "md",
};
